import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, BarElement } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Card, Flex, Grid, Heading, Skeleton, Span, Table } from '@chakra-ui/react';
import { useState, type JSX } from 'react';
import { priceFormatter, useMobileContext } from '@shared';
import { useGetMonthlyApprovedAccountsByYear, useGetMonthlyCreditsSpentByYear, useGetMonthlyMessagesSentByYear, useGetMonthlySalesByYear } from '../../api/hooks/useStatistics';
import { LuTrendingUp, LuTrendingDown } from 'react-icons/lu';

type StatisticsType = {
	[month: string]: number;
};

ChartJS.register(Filler, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);
const MONTHS_LENGTH = 12;

function DiffBadge({ current, previous }: { current: number; previous: number }): JSX.Element | null {
	if (previous === 0) {
		return current === 0 ? null : (
			<Flex as="span" alignItems="center" gap={1} color="green.600" fontSize="xs" whiteSpace="nowrap">
				<LuTrendingUp />
				100%
			</Flex>
		);
	}

	const diffPercentage = ((current - previous) / previous) * 100;

	if (diffPercentage === 0) {
		return null;
	}

	const isPositive = diffPercentage > 0;

	return (
		<Flex as="span" alignItems="center" gap={1} color={isPositive ? 'green.600' : 'red.600'} fontSize="xs" whiteSpace="nowrap">
			{isPositive ? <LuTrendingUp /> : <LuTrendingDown />}
			{Math.abs(diffPercentage).toFixed(0)}%
		</Flex>
	);
}

export default function StatisticsDataWrapper() {
	const now = new Date();

	const [selectedYear, setSelectedYear] = useState<string>(now.getFullYear().toString());
	const previousYear = (parseInt(selectedYear, 10) - 1).toString();

	const { data: monthlySales, isPending: isPendingSales } = useGetMonthlySalesByYear(selectedYear);
	const { data: monthlyApprovedAccounts, isPending: isPendingAccounts } = useGetMonthlyApprovedAccountsByYear(selectedYear);
	const { data: monthlyCreditsSpent, isPending: isPendingCredits } = useGetMonthlyCreditsSpentByYear(selectedYear);
	const { data: monthlyMessagesSent, isPending: isPendingMessages } = useGetMonthlyMessagesSentByYear(selectedYear);

	const { data: monthlySalesPreviousYear, isPending: isPendingSalesPreviousYear } = useGetMonthlySalesByYear(previousYear);
	const { data: monthlyApprovedAccountsPreviousYear, isPending: isPendingAccountsPreviousYear } = useGetMonthlyApprovedAccountsByYear(previousYear);
	const { data: monthlyCreditsSpentPreviousYear, isPending: isPendingCreditsPreviousYear } = useGetMonthlyCreditsSpentByYear(previousYear);
	const { data: monthlyMessagesSentPreviousYear, isPending: isPendingMessagesPreviousYear } = useGetMonthlyMessagesSentByYear(previousYear);

	// Each card is fetched independently and rendered (skeleton -> chart) as soon as its own
	// current+previous year requests resolve, instead of blocking the whole section on all 8
	// requests - the slowest metric no longer holds up the ones that already came back.
	return (
		<Statistics
			monthlySales={monthlySales ?? {}}
			monthlyApprovedAccounts={monthlyApprovedAccounts ?? {}}
			monthlyCreditsSpent={monthlyCreditsSpent ?? {}}
			monthlyMessagesSent={monthlyMessagesSent ?? {}}
			monthlySalesPreviousYear={monthlySalesPreviousYear ?? {}}
			monthlyApprovedAccountsPreviousYear={monthlyApprovedAccountsPreviousYear ?? {}}
			monthlyCreditsSpentPreviousYear={monthlyCreditsSpentPreviousYear ?? {}}
			monthlyMessagesSentPreviousYear={monthlyMessagesSentPreviousYear ?? {}}
			isPendingSalesCard={isPendingSales || isPendingSalesPreviousYear}
			isPendingAccountsCard={isPendingAccounts || isPendingAccountsPreviousYear}
			isPendingCreditsCard={isPendingCredits || isPendingCreditsPreviousYear}
			isPendingMessagesCard={isPendingMessages || isPendingMessagesPreviousYear}
			selectedYear={selectedYear}
			previousYear={previousYear}
			setSelectedYear={setSelectedYear}
		/>
	);
}

function Statistics({
	monthlySales,
	monthlyApprovedAccounts,
	monthlyCreditsSpent,
	monthlyMessagesSent,
	monthlySalesPreviousYear,
	monthlyApprovedAccountsPreviousYear,
	monthlyCreditsSpentPreviousYear,
	monthlyMessagesSentPreviousYear,
	isPendingSalesCard,
	isPendingAccountsCard,
	isPendingCreditsCard,
	isPendingMessagesCard,
	selectedYear,
	previousYear,
	setSelectedYear,
}: {
	monthlySales: StatisticsType;
	monthlyApprovedAccounts: StatisticsType;
	monthlyCreditsSpent: StatisticsType;
	monthlyMessagesSent: StatisticsType;
	monthlySalesPreviousYear: StatisticsType;
	monthlyApprovedAccountsPreviousYear: StatisticsType;
	monthlyCreditsSpentPreviousYear: StatisticsType;
	monthlyMessagesSentPreviousYear: StatisticsType;
	isPendingSalesCard: boolean;
	isPendingAccountsCard: boolean;
	isPendingCreditsCard: boolean;
	isPendingMessagesCard: boolean;
	selectedYear: string;
	previousYear: string;
	setSelectedYear: (year: string) => void;
}) {
	const { isMobile } = useMobileContext();
	const { t } = useTranslation();

	const [chartTypes, setChartTypes] = useState<{ chart: string; type: string }[]>(() => {
		const stored = localStorage.getItem('chartTypes');
		if (stored) return JSON.parse(stored);
		return [
			{ chart: 'monthlySales', type: 'line' },
			{ chart: 'monthlyCreditsSpent', type: 'line' },
			{ chart: 'monthlyMessagesSent', type: 'bar' },
			{ chart: 'monthlyAccountsCreated', type: 'line' },
		];
	});

	const monthlySalesCurrent = Array(MONTHS_LENGTH).fill(0);
	const monthlyCreditsSpentCurrent = Array(MONTHS_LENGTH).fill(0);
	const monthlyMessagesSentCurrent = Array(MONTHS_LENGTH).fill(0);
	const monthlyAccountsCreatedCurrent = Array(MONTHS_LENGTH).fill(0);

	const monthlySalesPrevious = Array(MONTHS_LENGTH).fill(0);
	const monthlyCreditsSpentPrevious = Array(MONTHS_LENGTH).fill(0);
	const monthlyMessagesSentPrevious = Array(MONTHS_LENGTH).fill(0);
	const monthlyAccountsCreatedPrevious = Array(MONTHS_LENGTH).fill(0);

	const labels = [
		t('general.january'),
		t('general.february'),
		t('general.march'),
		t('general.april'),
		t('general.may'),
		t('general.june'),
		t('general.july'),
		t('general.august'),
		t('general.september'),
		t('general.october'),
		t('general.november'),
		t('general.december'),
	];

	Object.entries(monthlySales)?.forEach(([key, value]) => {
		monthlySalesCurrent[parseInt(key) - 1] += value;
	});

	Object.entries(monthlyCreditsSpent)?.forEach(([key, value]) => {
		monthlyCreditsSpentCurrent[parseInt(key) - 1] += value;
	});

	Object.entries(monthlyMessagesSent)?.forEach(([key, value]) => {
		monthlyMessagesSentCurrent[parseInt(key) - 1] += value;
	});

	Object.entries(monthlyApprovedAccounts)?.forEach(([key, value]) => {
		monthlyAccountsCreatedCurrent[parseInt(key) - 1] += value;
	});

	Object.entries(monthlySalesPreviousYear)?.forEach(([key, value]) => {
		monthlySalesPrevious[parseInt(key) - 1] += value;
	});

	Object.entries(monthlyCreditsSpentPreviousYear)?.forEach(([key, value]) => {
		monthlyCreditsSpentPrevious[parseInt(key) - 1] += value;
	});

	Object.entries(monthlyMessagesSentPreviousYear)?.forEach(([key, value]) => {
		monthlyMessagesSentPrevious[parseInt(key) - 1] += value;
	});

	Object.entries(monthlyApprovedAccountsPreviousYear)?.forEach(([key, value]) => {
		monthlyAccountsCreatedPrevious[parseInt(key) - 1] += value;
	});

	function buildComparisonDataset(data: number[]) {
		return {
			label: `${previousYear}`,
			data,
			borderColor: 'rgb(148, 163, 184)',
			backgroundColor: 'rgb(148, 163, 184, 0.25)',
			borderDash: [6, 4],
			fill: false,
			tension: 0.2,
			pointBackgroundColor: 'white',
			pointBorderColor: 'rgb(148, 163, 184)',
			pointRadius: 3,
			pointHoverRadius: 5,
		};
	}

	const monthlyCreditsSpentData = {
		labels,
		datasets: [
			{
				label: `${selectedYear}`,
				data: monthlyCreditsSpentCurrent,
				borderColor: 'rgb(61, 213, 152)',
				backgroundColor: 'rgb(61, 213, 152, 0.6)',
				fill: {
					target: 'origin',
				},
				tension: 0.2,
				pointBackgroundColor: 'white',
				pointBorderColor: 'rgb(61, 213, 152)',
				pointRadius: 4,
				pointHoverRadius: 6,
			},
			buildComparisonDataset(monthlyCreditsSpentPrevious),
		],
	};

	const monthlySalesData = {
		labels,
		datasets: [
			{
				label: `${selectedYear}`,
				data: monthlySalesCurrent,
				borderColor: 'rgb(61, 213, 152)',
				backgroundColor: 'rgb(61, 213, 152, 0.6)',
				fill: {
					target: 'origin',
				},
				tension: 0.2,
				pointBackgroundColor: 'white',
				pointBorderColor: 'rgb(61, 213, 152)',
				pointRadius: 4,
				pointHoverRadius: 6,
			},
			buildComparisonDataset(monthlySalesPrevious),
		],
	};

	const monthlySalesOptions = {
		responsive: true,
		plugins: {
			tooltip: {
				callbacks: {
					label: (context: { parsed: { y: number } }) => {
						const value = context.parsed.y || 0;
						return new Intl.NumberFormat('fi-FI', {
							style: 'currency',
							currency: 'EUR',
						}).format(value);
					},
				},
			},
		},
		scales: {
			y: {
				min: 0,
				suggestedMax: 20_000,
				ticks: {
					callback: (value: number | string) =>
						new Intl.NumberFormat('fi-FI', {
							style: 'currency',
							currency: 'EUR',
						}).format(Number(value)),
				},
			},
		},
	};

	const monthlyCreditsSpentOptions = {
		responsive: true,
		scales: {
			y: {
				min: 0,
				suggestedMax: 200_000,
				ticks: {
					stepSize: 10_000,
				},
			},
		},
	};

	const monthlyMessagesSentData = {
		labels,
		datasets: [
			{
				label: `${selectedYear}`,
				data: monthlyMessagesSentCurrent,
				borderColor: 'rgb(61, 213, 152)',
				backgroundColor: 'rgb(61, 213, 152, 0.6)',
				fill: {
					target: 'origin',
				},
				tension: 0.2,
				pointBackgroundColor: 'white',
				pointBorderColor: 'rgb(61, 213, 152)',
				pointRadius: 4,
				pointHoverRadius: 6,
			},
			buildComparisonDataset(monthlyMessagesSentPrevious),
		],
	};

	const monthlyMessagesSentOptions = {
		responsive: true,
		scales: {
			y: {
				min: 0,
				suggestedMax: 100_000,
				ticks: {
					stepSize: 10000,
				},
			},
		},
	};

	const monthlyAccountsCreatedData = {
		labels,
		datasets: [
			{
				label: `${selectedYear}`,
				data: monthlyAccountsCreatedCurrent,
				borderColor: 'rgb(61, 213, 152)',
				backgroundColor: 'rgb(61, 213, 152, 0.6)',
				fill: {
					target: 'origin',
				},
				tension: 0.2,
				pointBackgroundColor: 'white',
				pointBorderColor: 'rgb(61, 213, 152)',
				pointRadius: 4,
				pointHoverRadius: 6,
			},
			buildComparisonDataset(monthlyAccountsCreatedPrevious),
		],
	};

	const monthlyAccountsCreatedOptions = {
		responsive: true,
		scales: {
			y: {
				min: 0,
				suggestedMax: 20,
				ticks: {
					stepSize: 1,
				},
			},
		},
	};

	function handleChartTypeChange(chart: string, type: string): void {
		setChartTypes(prev => {
			const updated = prev.map(c => (c.chart === chart ? { ...c, type } : c));
			localStorage.setItem('chartTypes', JSON.stringify(updated));
			return updated;
		});
	}

	function handleYearChange(year: string): void {
		setSelectedYear(year);
	}

	function getChartComponent(chart: string, data: Data, options: Options): JSX.Element {
		const chartType = chartTypes.find(c => c.chart === chart)?.type ?? 'line';
		return chartType === 'bar' ? (
			<Bar height={isMobile ? '200' : '150'} options={options} data={data} />
		) : (
			<Line height={isMobile ? '200' : '150'} options={options} data={data} />
		);
	}

	const renderTable = () => {
		const datasets = [
			{ key: 'monthlySales', title: t('general.monthly_sales'), data: monthlySalesData },
			{ key: 'monthlyCreditsSpent', title: t('general.monthly_credits_spent'), data: monthlyCreditsSpentData },
			{ key: 'monthlyMessagesSent', title: t('general.monthly_messages_sent'), data: monthlyMessagesSentData },
			{ key: 'monthlyAccountsCreated', title: t('general.monthly_accounts_created'), data: monthlyAccountsCreatedData },
		];

		const tableLabels = datasets[0]?.data.labels;

		const totals = datasets.map(ds => {
			const values = ds.data.datasets[0]?.data ?? [];
			return values.reduce((sum, v) => sum + v, 0);
		});

		const previousYearTotals = datasets.map(ds => {
			const values = ds.data.datasets[1]?.data ?? [];
			return values.reduce((sum, v) => sum + v, 0);
		});

		const averages = datasets.map((_, idx) => {
			const count = tableLabels?.length;
			if (count) {
				return count > 0 ? totals[idx] / count : 0;
			}
		});

		const previousYearAverages = datasets.map((_, idx) => {
			const count = tableLabels?.length;
			if (count) {
				return count > 0 ? previousYearTotals[idx] / count : 0;
			}
		});

		return (
			<Table.ScrollArea maxW="full" width="100%">
				<Table.Root interactive size="sm" striped>
					<Table.Header>
						<Table.Row>
							<Table.ColumnHeader fontWeight="bold">{t('general.month')}</Table.ColumnHeader>
							{datasets.map(ds => (
								<Table.ColumnHeader fontWeight="bold" key={ds.key}>
									{ds.title}
								</Table.ColumnHeader>
							))}
						</Table.Row>
					</Table.Header>
					<Table.Body maxH="full" overflowY="auto">
						{labels?.map((label, i) => (
							<Table.Row key={label}>
								<Table.Cell fontWeight="bold">{label}</Table.Cell>
								{datasets.map(ds => {
									const current = ds.data.datasets[0]?.data[i] ?? 0;
									const previous = ds.data.datasets[1]?.data[i] ?? 0;
									return (
										<Table.Cell key={ds.key}>
											<Flex alignItems="center" gap={2}>
												<Span>{ds.key === 'monthlySales' ? priceFormatter(current) : current}</Span>
												<DiffBadge current={current} previous={previous} />
											</Flex>
										</Table.Cell>
									);
								})}
							</Table.Row>
						))}
						<Table.Row fontWeight="bold">
							<Table.Cell>{t('general.average_per_month')}</Table.Cell>
							{datasets.map((ds, idx) => (
								<Table.Cell key={ds.key}>
									<Flex alignItems="center" gap={2}>
										<Span>
											{ds.key === 'monthlySales' ? `${priceFormatter(averages[idx] || 0)}` : `${averages[idx]?.toFixed(2)} ${t('general.amount_short')}`}
										</Span>
										<DiffBadge current={averages[idx] || 0} previous={previousYearAverages[idx] || 0} />
									</Flex>
								</Table.Cell>
							))}
						</Table.Row>
						<Table.Row fontWeight="bold">
							<Table.Cell>{t('general.total_this_year')}</Table.Cell>
							{datasets.map((ds, idx) => (
								<Table.Cell key={ds.key}>
									<Flex alignItems="center" gap={2}>
										<Span>{ds.key === 'monthlySales' ? `${priceFormatter(totals[idx])}` : `${totals[idx]} ${t('general.amount_short')}`}</Span>
										<DiffBadge current={totals[idx]} previous={previousYearTotals[idx]} />
									</Flex>
								</Table.Cell>
							))}
						</Table.Row>
					</Table.Body>
				</Table.Root>
			</Table.ScrollArea>
		);
	};

	const renderCard = ({ chartKey, title, data, options, isPending }: { chartKey: string; title: string; data: Data; options: Options; isPending: boolean }) => {
		const totalThisYear = data.datasets[0]?.data.reduce((acc, val) => acc + val, 0) || 0;

		return (
			<Skeleton loading={isPending} w="100%" flex={1} minH="340px" borderRadius="l2">
				<Card.Root w="100%" flex={1} overflow="auto">
					<Card.Body display="flex" flexDirection="column" p={5} gap={5}>
						<Flex justifyContent={'space-between'} gap={2}>
							<Flex flexDir={'column'}>
								<Heading>{title}</Heading>
								<Span>
									{t('general.total_this_year')} {chartKey === 'monthlySales' ? priceFormatter(totalThisYear) : `${totalThisYear} ${t('general.amount_short')}`}
								</Span>
							</Flex>
							<Flex>
								<select
									style={{
										height: '40px',
										paddingInline: 'var(--tav-spacing-3)',
										borderRadius: 'var(--tav-radii-l2)',
										backgroundColor: 'white',
										border: 'var(--tav-colors-border) 1px solid',
										width: '180px',
									}}
									onChange={e => handleChartTypeChange(chartKey, e.target.value)}
									value={chartTypes.find(c => c.chart === chartKey)?.type ?? 'line'}
								>
									<option value="bar">{t('general.bar')}</option>
									<option value="line">{t('general.line')}</option>
								</select>
							</Flex>
						</Flex>
						{getChartComponent(chartKey, data, options)}
					</Card.Body>
				</Card.Root>
			</Skeleton>
		);
	};
	const START_YEAR = 2025;
	const currentYear = new Date().getFullYear();

	const yearOptions = Array.from({ length: currentYear - START_YEAR + 1 }, (_, i) => currentYear - i).filter(year => year >= START_YEAR);

	return (
		<>
			<Flex gap={2} alignItems={'center'} pl="5" maxW="2000px" w="100%">
				<Heading>Statistics</Heading>
				<select
					style={{
						height: '40px',
						paddingInline: 'var(--tav-spacing-3)',
						borderRadius: 'var(--tav-radii-l2)',
						backgroundColor: 'white',
						border: 'var(--tav-colors-border) 1px solid',
						width: '100px',
					}}
					onChange={e => handleYearChange(e.target.value)}
					value={selectedYear}
				>
					{yearOptions.map(year => (
						<option key={year} value={year}>
							{year}
						</option>
					))}
				</select>
			</Flex>
			<Grid templateColumns={{ base: '1fr', md: 'repeat(1, 1fr)', lg: 'repeat(1, 1fr)', xl: 'repeat(2, 1fr)' }} gap={4} maxW="2000px" w="100%" flex={1} minW="340px">
				{renderCard({ chartKey: 'monthlySales', title: t('general.monthly_sales'), data: monthlySalesData, options: monthlySalesOptions, isPending: isPendingSalesCard })}
				{renderCard({
					chartKey: 'monthlyCreditsSpent',
					title: t('general.monthly_credits_spent'),
					data: monthlyCreditsSpentData,
					options: monthlyCreditsSpentOptions,
					isPending: isPendingCreditsCard,
				})}
				{renderCard({
					chartKey: 'monthlyMessagesSent',
					title: t('general.monthly_messages_sent'),
					data: monthlyMessagesSentData,
					options: monthlyMessagesSentOptions,
					isPending: isPendingMessagesCard,
				})}
				{renderCard({
					chartKey: 'monthlyAccountsCreated',
					title: t('general.monthly_accounts_created'),
					data: monthlyAccountsCreatedData,
					options: monthlyAccountsCreatedOptions,
					isPending: isPendingAccountsCard,
				})}
			</Grid>
			<Skeleton loading={isPendingSalesCard || isPendingCreditsCard || isPendingMessagesCard || isPendingAccountsCard} maxW="2000px" width="100%" mt={2} minH="200px" borderRadius="l2">
				<Card.Root maxW="2000px" width="100%" mt={2}>
					<Card.Body display="flex" flexDirection="column" p={5} gap={5}>
						{renderTable()}
					</Card.Body>
				</Card.Root>
			</Skeleton>
		</>
	);
}

type Options = {
	responsive: boolean;
	plugins?: {};
	scales: {
		y: {
			min: number;
			ticks: {
				stepSize?: number;
				callback?: (value: string | number) => string;
			};
		};
	};
};

type Data = {
	labels: string[];
	datasets: {
		label: string;
		data: number[];
		borderColor: string;
		backgroundColor: string;
		fill: boolean | { target: string };
		borderDash?: number[];
		tension: number;
		pointBackgroundColor: string;
		pointBorderColor: string;
		pointRadius: number;
		pointHoverRadius: number;
	}[];
};
